import { Asset, type AppBase, type ContainerResource, type Entity } from 'playcanvas';

type AvatarContainerResource = ContainerResource & {
    animations: Asset[];
};

/** 加载 Avatar GLB 并实例化为可渲染实体。 */
class AvatarModelLoader {
    private _app: AppBase;

    private _asset: Asset | null = null;

    constructor(app: AppBase) {
        this._app = app;
    }

    /**
     * 从 URL 或 object URL 加载 GLB 容器资源。
     *
     * @param url - GLB 资源地址。
     * @returns 实例化后的实体和动画资源列表。
     */
    load(url: string): Promise<{ entity: Entity; animations: Asset[] }> {
        this.unload();

        const filename = new URL(url, location.href).pathname.split('/').pop() || 'avatar.glb';
        const asset = new Asset(filename, 'container', { url, filename });
        this._asset = asset;

        return new Promise((resolve, reject) => {
            asset.on('load', () => {
                const resource = asset.resource as AvatarContainerResource;
                const entity = resource.instantiateRenderEntity({
                    castShadows: false,
                    receiveShadows: false
                });
                entity.name = 'avatar-root';
                this._app.root.addChild(entity);
                resolve({ entity, animations: resource.animations });
            });

            asset.on('error', (err: Error) => {
                reject(new Error(`[AvatarModelLoader] 加载 Avatar GLB 失败: ${url} - ${err.message}`));
            });

            this._app.assets.add(asset);
            this._app.assets.load(asset);
        });
    }

    /** 释放当前加载的 GLB 资源。 */
    unload(): void {
        if (this._asset) {
            if (this._asset.resource) {
                this._asset.unload();
            }
            this._app.assets.remove(this._asset);
            this._asset = null;
        }
    }
}

export { AvatarModelLoader };
